"use client";

import { useState } from "react";
import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";
import { Monogram } from "@/components/persona/Monogram";
import { LtrToken } from "@/components/cdm/LtrToken";
import { advanceScreen } from "@/lib/presenter/advanceScreen";

/**
 * Screen 5.1 — Reverse-corridor discovery, with Bridging Beat B folded in.
 *
 *   Per Bridging Beat B (20s): "Same engine. Reverse direction. Different
 *   commodity, different banks, different regulators." Web rotates to centre
 *   Pampagro ↔ Gulf Phosphate Industries (UAE).
 *
 *   Per 5.1: Pampagro browses the seller side of the graph filtered for
 *   fertilizer suppliers in the UAE. Picks Gulf Phosphate Industries. The
 *   Buyer Trust Profile pattern flips to a Seller Trust Profile.
 *
 *   Interaction model:
 *     - Click any row body → previewed in the right pane (soft highlight)
 *     - Click that row's Select button → committed (✓ badge + strong glow)
 *     The right pane always reflects the previewed row.
 */

type Seller = {
  orbid: string;
  legalName: string;
  initials: string;
  flag: string;
  location: string;
  primaryCommodities: string[];
  verifiedTrades: number;
  onTimePct: number;
  primaryCorridor: string;
  lifetimeVolumeLabel: string;
};

const UAE_SELLERS: Seller[] = [
  {
    orbid: "orbid:5493-gulfphosphate-ae",
    legalName: "Gulf Phosphate Industries UAE",
    initials: "GP",
    flag: "AE",
    location: "Abu Dhabi Port",
    primaryCommodities: ["Urea", "TSP", "Sulfur"],
    verifiedTrades: 18,
    onTimePct: 91,
    primaryCorridor: "AE → BR",
    lifetimeVolumeLabel: "USD 47M",
  },
  {
    orbid: "orbid:5493-ruwais-fert-ae",
    legalName: "Ruwais Fertilizer Industries",
    initials: "RF",
    flag: "AE",
    location: "Ruwais",
    primaryCommodities: ["Urea", "Ammonia"],
    verifiedTrades: 12,
    onTimePct: 87,
    primaryCorridor: "AE → IN",
    lifetimeVolumeLabel: "USD 31M",
  },
  {
    orbid: "orbid:5493-emirates-chem-ae",
    legalName: "Emirates Chem Holdings",
    initials: "EC",
    flag: "AE",
    location: "Abu Dhabi Port",
    primaryCommodities: ["TSP", "Sulfur", "DAP"],
    verifiedTrades: 9,
    onTimePct: 84,
    primaryCorridor: "AE → VN",
    lifetimeVolumeLabel: "USD 18M",
  },
];

export function ReverseCorridorDiscovery({ className }: { className?: string }) {
  const tBridge  = useTranslations("bridge");
  const t        = useTranslations("t3Scenario");
  const [previewedOrbid, setPreviewedOrbid] = useState<string>(UAE_SELLERS[0].orbid);
  const [confirmedOrbid, setConfirmedOrbid] = useState<string | null>(null);
  const featured = UAE_SELLERS.find((s) => s.orbid === previewedOrbid) ?? UAE_SELLERS[0];

  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass relative overflow-hidden p-5">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-state-traced to-transparent" />
        <div className="flex items-center gap-3">
          <span className="rounded-full border border-state-traced/40 bg-state-traced/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-state-traced">
            {tBridge("bBadge")}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-orbitra-muted">{tBridge("bSubtag")}</span>
        </div>
        <h2 className="mt-3 font-display text-2xl font-semibold text-orbitra-paper">{tBridge("bHeadline")}</h2>
        <p className="mt-1 text-[13px] text-state-traced">{tBridge("bSubcopy")}</p>
      </div>

      <div className="grid flex-1 grid-cols-3 gap-4">
        {/* Pampagro's social trade graph filter on the left */}
        <div className="glass col-span-2 flex flex-col p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
                {t("discoveryGraphKicker")}
              </p>
              <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">
                {t("discoveryGraphHeading")}
              </h3>
            </div>
            <div className="flex items-center gap-2 font-mono text-[10px]">
              <span className="rounded-md border border-state-traced/30 bg-orbitra-ink/40 px-2 py-1 text-state-traced">{t("discoveryFilterCommodity")}</span>
              <span className="rounded-md border border-state-traced/30 bg-orbitra-ink/40 px-2 py-1 text-state-traced">{t("discoveryFilterJurisdiction")}</span>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2">
            {UAE_SELLERS.map((s) => (
              <SellerRow
                key={s.orbid}
                seller={s}
                previewed={previewedOrbid === s.orbid}
                confirmed={confirmedOrbid === s.orbid}
                onPreview={() => setPreviewedOrbid(s.orbid)}
                onSelect={() => { setPreviewedOrbid(s.orbid); setConfirmedOrbid(s.orbid); }}
              />
            ))}
          </div>

          <div className="mt-auto rounded-md border border-state-verified/20 bg-orbitra-ink/40 p-3 text-[11px]">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">{t("discoveryBuyerProfileKicker")}</p>
            <p className="mt-1 text-orbitra-paper">
              <span className="text-orbitra-teal-soft">{t("discoveryBuyerStatVerified")}</span>
              <span className="mx-2 text-orbitra-muted">·</span>
              <span className="text-orbitra-teal-soft">{t("discoveryBuyerStatVolume")}</span>
              <span className="mx-2 text-orbitra-muted">·</span>
              <span className="text-orbitra-teal-soft">{t("discoveryBuyerStatOnTime")}</span>
              <span className="mx-2 text-orbitra-muted">·</span>
              <span className="font-mono text-orbitra-teal-soft">{t("discoveryBuyerStatBank")}</span>
            </p>
            <p className="mt-1 text-[10px] text-orbitra-muted">
              {t("discoveryBuyerBlurb")}
            </p>
          </div>
        </div>

        {/* Featured Seller Trust Profile — reflects the currently previewed row */}
        <div className="glass flex flex-col p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
            {confirmedOrbid === featured.orbid ? t("discoverySelectedLabelPanel") : t("discoveryPreviewingLabel")}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Monogram name={featured.legalName} flag={featured.flag} size={44} accent="var(--state-verified)" />
            <div className="flex flex-col">
              <p className="font-display text-base font-semibold text-orbitra-paper" dir="ltr">{featured.legalName}</p>
              <p className="font-mono text-[10px] text-orbitra-muted"><LtrToken>{featured.orbid}</LtrToken></p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
            <Stat label={t("discoveryStatVerifiedTrades")} value={String(featured.verifiedTrades)} />
            <Stat label={t("discoveryStatOnTime")}         value={`${featured.onTimePct}%`} />
            <Stat label={t("discoveryStatVolume")}         value={featured.lifetimeVolumeLabel} />
            <Stat label={t("discoveryStatDisputes")}       value="0" />
            <Stat label={t("discoveryStatCorridor")}       value={featured.primaryCorridor} />
            <Stat label={t("discoveryStatPort")}           value={featured.location} />
          </div>

          <div className="mt-4 flex flex-wrap gap-1">
            {featured.primaryCommodities.map((c) => (
              <span key={c} className="rounded-full border border-state-verified/30 bg-state-verified/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-state-verified">
                {c}
              </span>
            ))}
          </div>

          <div className="mt-auto pt-4 text-[11px]">
            <button type="button" onClick={advanceScreen} className="cta-primary w-full py-2 text-[12px]">
              {t("discoveryOpenQuote")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SellerRow({
  seller, previewed, confirmed, onPreview, onSelect,
}: {
  seller: Seller;
  previewed: boolean;
  confirmed: boolean;
  onPreview: () => void;
  onSelect: () => void;
}) {
  const t = useTranslations("t3Scenario");
  return (
    <div
      onClick={onPreview}
      className={clsx(
        "flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition-colors duration-state",
        confirmed
          ? "border-state-verified/60 bg-state-verified/10 shadow-[0_0_18px_var(--state-verified-glow)]"
          : previewed
            ? "border-state-traced/50 bg-state-traced/8"
            : "border-state-verified/20 bg-orbitra-ink/40 hover:border-state-traced/40 hover:bg-state-traced/5",
      )}
    >
      <Monogram name={seller.legalName} flag={seller.flag} size={32} accent={confirmed ? "var(--state-verified)" : previewed ? "var(--state-traced)" : "var(--state-cold)"} />
      <div className="flex flex-1 flex-col">
        <p className="text-[12px] font-semibold text-orbitra-paper" dir="ltr">{seller.legalName}</p>
        <p className="font-mono text-[9px] text-orbitra-muted" dir="ltr">{seller.orbid} · {seller.location}</p>
      </div>
      <div className="hidden flex-col items-end gap-0.5 text-[10px] sm:flex">
        <span className="text-orbitra-paper">{seller.verifiedTrades} {t("discoveryTradesUnit")}</span>
        <span className="text-orbitra-muted">{seller.onTimePct}% {t("discoveryOnTimeSuffix")} · {seller.primaryCorridor}</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {seller.primaryCommodities.map((c) => (
          <span key={c} className="rounded border border-state-traced/30 bg-state-traced/10 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-state-traced">
            {c}
          </span>
        ))}
      </div>
      {confirmed ? (
        <span className="ms-2 rounded-full bg-state-verified px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider text-orbitra-ink">
          {t("discoverySelectedLabel")} ✓
        </span>
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSelect(); }}
          className="ms-2 rounded-full border border-state-verified/40 bg-state-verified/10 px-3 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider text-state-verified hover:bg-state-verified/20"
        >
          {t("discoverySelectButton")}
        </button>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-2 py-1.5">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className="mt-0.5 text-orbitra-paper">{value}</p>
    </div>
  );
}
